import { useState, useEffect } from "react";
import { FORMATIONS } from "../formation";
import { normalizeTeam } from "../../../utils/normalizeTeam";

import FormationSelect from "./FormationSelection";
import Pitch from "./Pitch/Pitch";
import PlayerSwapModal from "./PlayerSwapModal";
import "./FormationBuilder.css";

/* -------------------------------------------
   Populate formation using ordered players
-------------------------------------------- */
function populateFormationByOrder(players, formation) {
  const lineup = {};
  let index = 0;

  Object.entries(formation.rows).forEach(([rowKey, slots]) => {
    const orderedSlots =
      rowKey === "keeper" ? slots : [...slots].reverse();

    orderedSlots.forEach((slot) => {
      if (!players[index]) return;
      lineup[slot] = players[index];
      index++;
    });
  });

  return lineup;
}

export default function FormationBuilder({ teamJson }) {
  const team = normalizeTeam(teamJson);

  const [formation, setFormation] = useState(team.formation);

  const [savedPlayers, setSavedPlayers] = useState([]);
  const [draftPlayers, setDraftPlayers] = useState([]);

  const [activeSlot, setActiveSlot] = useState(null);
  const [draggedPlayerId, setDraggedPlayerId] = useState(null);
const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /* -------------------------------------------
     INITIAL LOAD (ONCE)
  -------------------------------------------- */
  useEffect(() => {
    const ordered = [...team.players]
      .filter((p) => p.formationPlace)
      .sort(
        (a, b) =>
          Number(a.formationPlace) - Number(b.formationPlace)
      );

    setSavedPlayers(ordered);
    setDraftPlayers(ordered);
  }, []); // ✅ run once only

  /* -------------------------------------------
     Derived lineup (NO STATE)
  -------------------------------------------- */
  const draftLineup = populateFormationByOrder(
    draftPlayers,
    FORMATIONS[formation]
  );

  /* -------------------------------------------
     Swap via modal
  -------------------------------------------- */
const handleSwap = (slot, player) => {
  setDraftPlayers((prev) => {
    const targetPlayer = draftLineup[slot];
    if (!targetPlayer) return prev;

    const fromIndex = prev.findIndex(
      (p) => p.id === player.id
    );
    const toIndex = prev.findIndex(
      (p) => p.id === targetPlayer.id
    );

    if (fromIndex === -1 || toIndex === -1) return prev;

    const updated = [...prev];
    [updated[fromIndex], updated[toIndex]] = [
      updated[toIndex],
      updated[fromIndex],
    ];

    return updated;
  });

  setHasUnsavedChanges(true); // ✅ MARK DIRTY
};

  /* -------------------------------------------
     Drag & drop (row-scoped)
  -------------------------------------------- */
const handleRowReorder = (type, playerId) => {
  if (type === "START") {
    setDraggedPlayerId(playerId);
    return;
  }

  if (type === "DROP" && draggedPlayerId) {
    setDraftPlayers((prev) => {
      const fromIndex = prev.findIndex(
        (p) => p.id === draggedPlayerId
      );
      const toIndex = prev.findIndex(
        (p) => p.id === playerId
      );

      if (fromIndex === -1 || toIndex === -1) return prev;

      const updated = [...prev];
      [updated[fromIndex], updated[toIndex]] = [
        updated[toIndex],
        updated[fromIndex],
      ];

      return updated;
    });

    setHasUnsavedChanges(true); // ✅ MARK DIRTY
    setDraggedPlayerId(null);
  }
};


  /* -------------------------------------------
     Save / Cancel
  -------------------------------------------- */
const saveChanges = () => {
  const updated = draftPlayers.map((p, index) => ({
    ...p,
    formationPlace: index + 1,
  }));

  console.log("SEND TO BACKEND:", updated);

  setSavedPlayers(updated);
  setDraftPlayers(updated);
  setActiveSlot(null);
  setHasUnsavedChanges(false); // ✅ RESET
};

const cancelChanges = () => {
  setDraftPlayers(savedPlayers);
  setActiveSlot(null);
  setHasUnsavedChanges(false); // ✅ RESET
};


  return (
    <div className="formation-container">
<FormationSelect
  formations={FORMATIONS}
  value={formation}
  onChange={(f) => {
    setFormation(f);
    setHasUnsavedChanges(true); // ✅ MARK DIRTY
  }}
/>


      <Pitch
        formation={FORMATIONS[formation]}
        lineup={draftLineup}
        onSlotClick={setActiveSlot}
        onRowReorder={handleRowReorder}
      />
{hasUnsavedChanges && (
  <div className="save-bar">
    <span>You have unsaved changes</span>
    <button onClick={cancelChanges}>Cancel</button>
    <button className="primary" onClick={saveChanges}>
      Save
    </button>
  </div>
)}

      {activeSlot && (
        <PlayerSwapModal
          slot={activeSlot}
          players={draftPlayers}
          draftLineup={draftLineup}
          onSwap={handleSwap}
          onSave={saveChanges}
          onCancel={cancelChanges}
        />
      )}
    </div>
  );
}
